import type { SectionLinkItem } from '@/types/contentstack';
import { analyticsService } from '@utils/analytics';
import { resolveIconUrl } from '@utils/ids-icons';
import { sanitizeAttr, sanitizeCSS, sanitizeUrl } from '@utils/sanitizer';

const ID_PREFIX = 'social-link-module-links';

export interface ModuleBlock {
  title: string;
  icon_color: string;
  items: SectionLinkItem[];
}

function renderItem(item: SectionLinkItem, index: number, iconColor: string): string {
  const hidden = item.activated ? '' : ' hidden';
  const color = sanitizeCSS(iconColor);
  const iconStyle = color ? ` style="color: ${color};"` : '';
  const icon = item.icon
    ? `<idsw-icon url="${sanitizeUrl(resolveIconUrl(item.icon))}" size="medium" aria-hidden="true"${iconStyle}></idsw-icon>`
    : '';

  const leading = icon
    ? `<span class="idsw-list-item-default__leading-item">${icon}</span>`
    : '';

  return `
    <idsw-list-item${hidden}>
      <button
        type="button"
        class="idsw-list-item-default module-links__item"
        data-testid="${ID_PREFIX}-link-${index + 1}"
        data-href="${sanitizeUrl(item.link.href)}"
        data-label="${sanitizeAttr(item.link.title)}"
      >
        ${leading}
        <span class="idsw-list-item-default__content">
          <span class="idsw-list-item-default__label-box">
            <span class="idsw-list-item-default__label">${sanitizeAttr(item.link.title)}</span>
          </span>
          <span class="idsw-list-item-default__trailing-item" aria-hidden="true">
            <idsw-icon url="${sanitizeUrl(resolveIconUrl('arrow_right'))}" size="small"></idsw-icon>
          </span>
        </span>
      </button>
    </idsw-list-item>`;
}

export function renderModuleLinks(blocks: ModuleBlock[]): string {
  return blocks
    .map((block) => {
      const items = block.items
        .map((item, index) => renderItem(item, index, block.icon_color))
        .join('');

      return `
  <section data-block-title="${sanitizeAttr(block.title)}">
    <div class="ids-pt-6" data-testid="${ID_PREFIX}-block">
      <h2 class="ids-pb-4" data-testid="${ID_PREFIX}-block-title">${sanitizeAttr(block.title)}</h2>
    </div>
    <idsw-list gap="small" data-testid="${ID_PREFIX}-link-list">
      ${items}
    </idsw-list>
  </section>`;
    })
    .join('');
}

export function attachModuleLinksListeners(root: ParentNode): void {
  const sections = root.querySelectorAll<HTMLElement>('[data-block-title]');

  sections.forEach((section) => {
    const group = section.getAttribute('data-block-title') ?? '';
    const buttons = section.querySelectorAll<HTMLButtonElement>(
      `button[data-testid^="${ID_PREFIX}-link-"]`,
    );

    buttons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const href = button.dataset.href ?? '';
        const linkText = button.dataset.label ?? '';

        analyticsService.event({
          link: {
            category: 'Clique',
            action: group,
            label: `LNK:SocialLink:${group}:${linkText}`,
          },
        });

        if (href) {
          window.open(href, '_blank', 'noopener,noreferrer');
        }
      });
    });
  });
}
